import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import API from "../services/api";

const ModuleContext = createContext(null);

export function ModuleProvider({ children }) {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);

  const refreshModules = useCallback(async () => {
    try {
      const res = await API.get("/api/module-settings");
      const map = {};
      (Array.isArray(res.data) ? res.data : []).forEach((m) => {
        map[m.moduleKey] = !!m.enabled;
      });
      setModules(map);
    } catch {
      // Keep the last known switches. Resetting to {} here would make every
      // module look enabled whenever one refresh failed.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshModules();
  }, [refreshModules]);

  // Pick up switches flipped by another admin: re-check when the tab regains
  // focus, every 2 minutes, and immediately when an API call answers 423
  // "module disabled" (see services/api.js).
  useEffect(() => {
    let last = 0;
    const refreshSoon = () => {
      const now = Date.now();
      if (now - last < 5000) return;   // throttle bursts of 423s / focus events
      last = now;
      refreshModules();
    };
    const onVisible = () => { if (document.visibilityState === "visible") refreshSoon(); };
    window.addEventListener("prosale:module-disabled", refreshSoon);
    window.addEventListener("focus", refreshSoon);
    document.addEventListener("visibilitychange", onVisible);
    const timer = setInterval(refreshSoon, 120000);
    return () => {
      window.removeEventListener("prosale:module-disabled", refreshSoon);
      window.removeEventListener("focus", refreshSoon);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(timer);
    };
  }, [refreshModules]);

  const isModuleEnabled = useCallback((key) => modules[key] !== false, [modules]);

  const value = useMemo(() => ({ modules, loading, isModuleEnabled, refreshModules }), [modules, loading, isModuleEnabled, refreshModules]);
  return <ModuleContext.Provider value={value}>{children}</ModuleContext.Provider>;
}

export function useModules() {
  const ctx = useContext(ModuleContext);
  return ctx || { modules: {}, loading: false, isModuleEnabled: () => true, refreshModules: () => {} };
}
